/** Canonical page keys. New pages go here (and PAGE_LABELS + PAGE_PATH_BY_KEY). Dev admin stubs in App.tsx / authContext.tsx must spread this list — do not keep a parallel copy. */
export const APP_PAGE_KEYS = [
  "assistant",
  "orders",
  "products",
  "amz-products",
  "product-planning",
  "dashboard",
  "modes",
  "events",
  "keywords",
  "tags",
  "runs",
  "dayparting",
  "listing-audit",
  "account-health",
  "negatives",
  "harvesting",
  "repricer",
  "portfolios",
  "marketplaces",
  "settings",
  "history",
  "users",
] as const

export type AppPageKey = typeof APP_PAGE_KEYS[number]

/** Primary nav path for each app page (used for routes and chunk prefetch). */
export const PAGE_PATH_BY_KEY: Record<AppPageKey, string> = {
  assistant: "/assistant",
  orders: "/orders",
  products: "/products",
  "amz-products": "/amz-products",
  "product-planning": "/product-planning",
  dashboard: "/dashboard",
  modes: "/optimization/modes",
  events: "/optimization/events",
  keywords: "/optimization/keywords",
  tags: "/optimization/tags",
  runs: "/optimization",
  dayparting: "/dayparting",
  "listing-audit": "/listing-audit",
  "account-health": "/account-health",
  negatives: "/negatives",
  harvesting: "/harvesting",
  repricer: "/repricer",
  portfolios: "/optimization/portfolios",
  marketplaces: "/optimization/marketplaces",
  settings: "/settings",
  history: "/history",
  users: "/users",
}

/** Nav paths without their own AppPageKey (reuse parent page chunk for prefetch). */
export const PAGE_SECONDARY_CHUNK_PATHS = [ "/amz-products/history", "/amz-products/fees" ] as const

/** RTDB key for Firebase Auth email (dots → commas). */
export function emailToRtdbKey(email: string): string {
  return email.trim().toLowerCase().replace(/\./g, ",")
}

export function normalizeAppEmail(email: string): string {
  return email.trim().toLowerCase()
}

export function canonicalAppEmail(email: string): string {
  return normalizeAppEmail(email)
}

export function authEmailsFor(email: string, extra: string[] = []): string[] {
  const canonical = canonicalAppEmail(email)
  const out = new Set<string>([ canonical ])
  for (const x of extra) {
    const n = normalizeAppEmail(x)
    if (n.includes("@")) out.add(n)
  }
  return [ ...out ]
}

export function permissionsFromAppUser(
  row: {
    is_admin?: boolean
    allowed_pages?: string[]
    email?: string
    display_name?: string | null
  },
  fallbackEmail?: string,
): { allowed: true; isAdmin: boolean; allowedPages: AppPageKey[]; email?: string; displayName?: string | null } {
  const isAdmin = Boolean(row.is_admin)
  const allowedSet = new Set<string>(APP_PAGE_KEYS)
  const allowedPages = (isAdmin
    ? [ ...APP_PAGE_KEYS ]
    : (row.allowed_pages ?? []).filter(p => allowedSet.has(p))
  ) as AppPageKey[]
  return {
    allowed: true,
    isAdmin,
    allowedPages,
    email: row.email || fallbackEmail,
    displayName: row.display_name ?? null,
  }
}

export const OPTIMIZATION_TAB_KEYS = [
  "runs",
  "keywords",
  "tags",
  "modes",
  "events",
  "portfolios",
  "marketplaces",
] as const

export type OptimizationTabKey = typeof OPTIMIZATION_TAB_KEYS[number]

export const OPTIMIZATION_TABS: { key: OptimizationTabKey; label: string; path: string }[] = [
  { key: "runs", label: "Runs", path: PAGE_PATH_BY_KEY.runs },
  { key: "keywords", label: "Keywords", path: PAGE_PATH_BY_KEY.keywords },
  { key: "tags", label: "Tags", path: PAGE_PATH_BY_KEY.tags },
  { key: "modes", label: "Modes", path: PAGE_PATH_BY_KEY.modes },
  { key: "events", label: "Events", path: PAGE_PATH_BY_KEY.events },
  { key: "portfolios", label: "Portfolios", path: PAGE_PATH_BY_KEY.portfolios },
  { key: "marketplaces", label: "Marketplaces", path: PAGE_PATH_BY_KEY.marketplaces },
]

/** Hub tabs for sections guarded by a single page key (see RoutedPageHub). */
export const HARVESTING_TABS: { key: string; label: string; path: string }[] = [
  { key: "rules", label: "Rules", path: "/harvesting" },
  { key: "candidates", label: "Candidates", path: "/harvesting/candidates" },
]

export const REPRICER_TABS: { key: string; label: string; path: string }[] = [
  { key: "rules", label: "Rules", path: "/repricer" },
  { key: "candidates", label: "Candidates", path: "/repricer/candidates" },
]

export const runDetailPath = (date: string): string => `/optimization/runs/${date}`

export function optimizationTabFromPath(pathname: string): OptimizationTabKey {
  const path = pathname.replace(/\/+$/, "") || "/"
  if (path.startsWith("/optimization/keywords")) return "keywords"
  if (path.startsWith("/optimization/tags")) return "tags"
  if (path.startsWith("/optimization/modes")) return "modes"
  if (path.startsWith("/optimization/events")) return "events"
  if (path.startsWith("/optimization/portfolios")) return "portfolios"
  if (path.startsWith("/optimization/marketplaces")) return "marketplaces"
  return "runs"
}

export const PAGE_LABELS: Record<AppPageKey, string> = {
  assistant: "Assistant",
  orders: "Orders",
  products: "KFL Products",
  "amz-products": "AMZ Products",
  "product-planning": "Product Planning",
  dashboard: "Dashboard",
  modes: "Modes",
  events: "Events",
  keywords: "Keywords",
  tags: "Tags",
  runs: "Runs",
  dayparting: "Dayparting",
  "listing-audit": "Listing Health",
  "account-health": "Account Health",
  harvesting: "Harvesting",
  repricer: "Repricer",
  negatives: "Negatives",
  portfolios: "Portfolios",
  marketplaces: "Marketplaces",
  settings: "Settings",
  history: "Change history",
  users: "Users",
}

/** Preferred grouping for the Users access picker (aligned with sidebar sections). */
export const PAGE_GROUPS: { label: string; keys: AppPageKey[] }[] = [
  { label: "Assistant", keys: [ "assistant" ] },
  { label: "Amazon", keys: [ "amz-products", "product-planning", "listing-audit", "account-health", "repricer" ] },
  { label: "PPC", keys: [ "dashboard", "dayparting", "negatives", "harvesting" ] },
  { label: "Optimization", keys: [ ...OPTIMIZATION_TAB_KEYS ] },
  { label: "Kaufland", keys: [ "orders", "products" ] },
  { label: "System", keys: [ "users", "history", "settings" ] },
]

/**
 * Groups for the Users UI. Any key in APP_PAGE_KEYS that is not listed in PAGE_GROUPS
 * is appended under "Other", so new pages appear automatically once added to APP_PAGE_KEYS + PAGE_LABELS.
 */
export function pageGroupsForAccessUi(): { label: string; keys: AppPageKey[] }[] {
  const grouped = new Set(PAGE_GROUPS.flatMap(g => g.keys))
  const ungrouped = APP_PAGE_KEYS.filter(k => !grouped.has(k))
  if (ungrouped.length === 0) return PAGE_GROUPS
  return [ ...PAGE_GROUPS, { label: "Other", keys: ungrouped } ]
}

export const DEFAULT_HOME_PAGE: AppPageKey = "orders"

export function canAccessPage(allowedPages: string[] | undefined, pageKey: string): boolean {
  if (!allowedPages?.length) return false
  return allowedPages.includes(pageKey)
}

export function firstAllowedPage(allowedPages: string[] | undefined): AppPageKey {
  for (const key of APP_PAGE_KEYS) {
    if (allowedPages?.includes(key)) return key
  }
  return DEFAULT_HOME_PAGE
}
